import { useRef } from 'react';

/**
 * 公共打印工具Hook - 使用html2canvas生成图片后打印
 * @param {boolean} openCheck - Modal是否打开
 * @param {string} title - 打印文档标题（如"付款单"、"收款单"等）
 * @returns {Function} handlePrint - 打印处理函数
 */
export const usePrintModal = (openCheck, title = '文档') => {
    const modalRef = useRef(null);

    const handlePrint = async () => {
        if (!openCheck) {
            alert('请先打开Modal');
            return;
        }
        
        // 动态加载html2canvas
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                if (window.html2canvas) {
                    resolve();
                    return;
                }
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        };
        
        try {
            // 加载html2canvas
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
            
            // 等待一下确保Modal已经渲染
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // 优先使用 modalRef 指向的内容区域作为打印目标（包含「团队收入」等表格的 div）
            let actualContent = null;
            if (modalRef.current) {
                actualContent = modalRef.current;
            }
            
            // 如果 modalRef 没有找到内容，则查找所有 Modal，优先选择最外层、最大的 Modal
            if (!actualContent) {
                const allModalWraps = Array.from(document.querySelectorAll('.ant-modal-wrap'));
                
                // 过滤出可见的 Modal，并按 z-index 和宽度排序，优先选择最外层、最大的
                const visibleModals = allModalWraps
                    .map(wrap => {
                        const style = window.getComputedStyle(wrap);
                        const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
                        if (!isVisible) return null;
                        
                        const content = wrap.querySelector('.ant-modal-content') || wrap.querySelector('.ant-modal-body');
                        if (!content) return null;
                        
                        const contentStyle = window.getComputedStyle(content);
                        const zIndex = parseInt(contentStyle.zIndex) || 0;
                        const width = content.offsetWidth || 0;
                        
                        // 排除加载 Modal（通常宽度较小且居中）
                        const isCentered = wrap.classList.contains('ant-modal-centered');
                        const isSmallModal = width <= 400;
                        if (isCentered && isSmallModal) return null;
                        
                        return { wrap, content, zIndex, width };
                    })
                    .filter(Boolean)
                    .sort((a, b) => {
                        // 优先按 z-index 降序，然后按宽度降序
                        if (b.zIndex !== a.zIndex) return b.zIndex - a.zIndex;
                        return b.width - a.width;
                    });
                
                if (visibleModals.length > 0) {
                    const selectedModal = visibleModals[0];
                    actualContent = selectedModal.content;
                    
                    // 如果找到的是 modal-content，尝试找到 modal-body
                    if (actualContent.classList.contains('ant-modal-content')) {
                        const body = actualContent.querySelector('.ant-modal-body');
                        if (body) {
                            actualContent = body;
                        }
                    }
                }
            }
            
            if (!actualContent) {
                alert('未找到打印内容，请确保Modal已完全加载');
                return;
            }
            
            // 临时隐藏所有内层的加载 Modal 和其他小 Modal（避免遮挡）
            const allModalWraps = document.querySelectorAll('.ant-modal-wrap');
            const hiddenModals = [];
            const actualContentWrap = actualContent.closest('.ant-modal-wrap');
            
            allModalWraps.forEach(wrap => {
                // 跳过当前要打印的 Modal
                if (wrap === actualContentWrap) return;
                
                const content = wrap.querySelector('.ant-modal-content');
                if (!content) return;
                
                const style = window.getComputedStyle(wrap);
                const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
                if (!isVisible) return;
                
                const width = content.offsetWidth || 0;
                const isSmallModal = width <= 400;
                const isCentered = wrap.classList.contains('ant-modal-centered');
                
                // 隐藏内层的加载 Modal 或其他小 Modal（通常是居中的小 Modal）
                if (isSmallModal || (isCentered && isSmallModal)) {
                    const originalDisplay = wrap.style.display;
                    const originalVisibility = wrap.style.visibility;
                    wrap.style.display = 'none';
                    wrap.style.visibility = 'hidden';
                    hiddenModals.push({ wrap, originalDisplay, originalVisibility });
                }
            });
            
            // 临时隐藏不需要打印的元素
            const elementsToHide = actualContent.querySelectorAll(
                '.ant-modal-close, .ant-modal-footer, button, .ant-spin-container, .ant-spin'
            );
            const originalDisplays = [];
            elementsToHide.forEach((el, index) => {
                originalDisplays[index] = el.style.display;
                el.style.display = 'none';
            });
            
            // 删除Steps横线元素（临时隐藏）
            const stepsTails = actualContent.querySelectorAll('.ant-steps-item-tail');
            const originalTailDisplays = [];
            stepsTails.forEach((tail, index) => {
                originalTailDisplays[index] = tail.style.display;
                tail.style.display = 'none';
            });
            
            // 等待一下确保样式应用和图片加载
            await new Promise(resolve => setTimeout(resolve, 500));

            // 临时展开表格和所有可能裁剪内容的容器，使「团队收入」等表格完整参与布局
            const tableScrollEls = actualContent.querySelectorAll('.ant-table-content, .ant-table-body, .ant-table-container, .ant-table-wrapper');
            const savedTableStyles = [];
            tableScrollEls.forEach(el => {
                savedTableStyles.push({
                    overflow: el.style.overflow,
                    maxHeight: el.style.maxHeight,
                    height: el.style.height,
                });
                el.style.overflow = 'visible';
                el.style.maxHeight = 'none';
                el.style.height = 'auto';
            });
            const savedContentOverflow = actualContent.style.overflow;
            const savedContentMaxHeight = actualContent.style.maxHeight;
            const savedContentHeight = actualContent.style.height;
            actualContent.style.overflow = 'visible';
            actualContent.style.maxHeight = 'none';
            actualContent.style.height = 'auto';
            // 将捕获区域滚入视图，确保下方表格被渲染
            actualContent.scrollIntoView && actualContent.scrollIntoView({ block: 'start', behavior: 'instant' });
            let parentBody = null;
            let savedParentOverflow = '';
            let savedParentMaxHeight = '';
            let savedParentHeight = '';
            if (actualContent.closest) {
                parentBody = actualContent.closest('.ant-modal-body');
                if (parentBody) {
                    savedParentOverflow = parentBody.style.overflow;
                    savedParentMaxHeight = parentBody.style.maxHeight;
                    savedParentHeight = parentBody.style.height;
                    parentBody.style.overflow = 'visible';
                    parentBody.style.maxHeight = 'none';
                    parentBody.style.height = 'auto';
                }
            }
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // 确保所有图片都已加载
            const images = actualContent.querySelectorAll('img');
            await Promise.all(
                Array.from(images).map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = resolve; // 即使加载失败也继续
                        setTimeout(resolve, 2000); // 2秒超时
                    });
                })
            );
            
            let canvas;
            try {
                // 使用html2canvas生成图片
                canvas = await window.html2canvas(actualContent, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: actualContent.scrollWidth,
                height: actualContent.scrollHeight,
                windowWidth: actualContent.scrollWidth,
                windowHeight: actualContent.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                allowTaint: true,
                removeContainer: false,
                imageTimeout: 20000,
                letterRendering: true,
                onclone: (clonedDoc, clonedElement) => {
                    // 确保克隆文档中的图片也能正确显示
                    const clonedImages = clonedDoc.querySelectorAll('img');
                    clonedImages.forEach(img => {
                        if (img.src && !img.complete) {
                            // 如果图片未加载完成，尝试重新设置 src
                            const originalSrc = img.src;
                            img.src = '';
                            img.src = originalSrc;
                        }
                    });
                    // 展开表格和模态体的滚动容器
                    if (clonedElement) {
                        clonedElement.style.overflow = 'visible';
                        clonedElement.style.maxHeight = 'none';
                        clonedElement.style.height = 'auto';
                        const tableScroll = clonedElement.querySelectorAll('.ant-table-content, .ant-table-body, .ant-table-container, .ant-table-wrapper');
                        tableScroll.forEach(el => {
                            el.style.overflow = 'visible';
                            el.style.maxHeight = 'none';
                            el.style.height = 'auto';
                        });
                        // 团队收入：隐藏 antd Table 区域，显示打印用纯 HTML 表格（html2canvas 可正确渲染）
                        clonedElement.querySelectorAll('[data-print-table-wrap]').forEach(wrap => {
                            const screenTable = wrap.querySelector('[data-screen-table]');
                            const printTable = wrap.querySelector('[data-print-table]');
                            if (screenTable) screenTable.style.display = 'none';
                            if (printTable) printTable.style.display = 'block';
                        });
                    }
                }
            });
            } finally {
                // 无论成功或失败都恢复表格和 modal 容器的滚动样式
                tableScrollEls.forEach((el, index) => {
                    const s = savedTableStyles[index];
                    if (s) {
                        el.style.overflow = s.overflow || '';
                        el.style.maxHeight = s.maxHeight || '';
                        el.style.height = s.height || '';
                    }
                });
                actualContent.style.overflow = savedContentOverflow || '';
                actualContent.style.maxHeight = savedContentMaxHeight || '';
                actualContent.style.height = savedContentHeight || '';
                if (parentBody) {
                    parentBody.style.overflow = savedParentOverflow || '';
                    parentBody.style.maxHeight = savedParentMaxHeight || '';
                    parentBody.style.height = savedParentHeight || '';
                }
            }
            
            // 恢复隐藏的元素
            elementsToHide.forEach((el, index) => {
                el.style.display = originalDisplays[index] || '';
            });
            stepsTails.forEach((tail, index) => {
                tail.style.display = originalTailDisplays[index] || '';
            });
            
            // 恢复隐藏的内层 Modal
            hiddenModals.forEach(({ wrap, originalDisplay, originalVisibility }) => {
                wrap.style.display = originalDisplay || '';
                wrap.style.visibility = originalVisibility || '';
            });
            
            // 创建包含图片的打印页面
            const imgData = canvas.toDataURL('image/png', 1.0);
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('无法打开打印窗口，请允许弹出窗口');
                return;
            }
            
            const htmlContent = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        @page {
            margin: 10mm;
            size: A4;
        }
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        html, body {
            width: 100%;
            height: 100%;
            font-family: "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
        }
        body {
            display: flex;
            justify-content: center;
            align-items: flex-start;
            background: #fff;
            padding: 20px;
        }
        img {
            max-width: 100%;
            height: auto;
            display: block;
        }
        @media print {
            body {
                padding: 0;
            }
            img {
                width: 100%;
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <img src="${imgData}" alt="${title}" />
    <script>
        window.onload = function() {
            setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                    window.close();
                };
            }, 500);
        };
    </script>
</body>
</html>
            `;
            
            printWindow.document.open();
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            
        } catch (error) {
            console.error('打印失败:', error);
            alert('打印失败: ' + error.message);
        }
    };

    return { handlePrint, modalRef };
};
